import React from 'react';
import { Card } from './ui/Card';
import { PlayerStatsCard } from './ui/PlayerStatsCard';

export const PlayerCareerStats = ({ skaterStats = [], goalieStats = [], showTables = true }) => {
  // Calculate career totals for skaters
  const skaterCareerTotals = skaterStats.reduce(
    (totals, season) => ({
      gamesPlayed: (totals.gamesPlayed || 0) + (season.gp || 0),
      goals: (totals.goals || 0) + (season.g || 0),
      assists: (totals.assists || 0) + (season.a || 0),
      points: (totals.points || 0) + ((season.g || 0) + (season.a || 0)),
      penaltyMinutes: (totals.penaltyMinutes || 0) + (season.pim || 0),
      powerPlayGoals: (totals.powerPlayGoals || 0) + (season.ppg || 0),
      powerPlayAssists: (totals.powerPlayAssists || 0) + (season.ppa || 0),
      shortHandedGoals: (totals.shortHandedGoals || 0) + (season.shg || 0),
      overtimeGoals: (totals.overtimeGoals || 0) + (season.otg || 0),
      emptyNetGoals: (totals.emptyNetGoals || 0) + (season.eng || 0),
      minutesPlayed: (totals.minutesPlayed || 0) + (season.mins || 0),
    }),
    {}
  );

  // Calculate career totals for goalies
  const goalieCareerTotals = goalieStats.reduce(
    (totals, season) => ({
      gamesPlayed: (totals.gamesPlayed || 0) + (season.gp || 0),
      shotsAgainst: (totals.shotsAgainst || 0) + (season.sa || 0),
      goalsAgainst: (totals.goalsAgainst || 0) + (season.ga || 0),
      saves: (totals.saves || 0) + (season.sv || 0),
      minutesPlayed: (totals.minutesPlayed || 0) + (season.mins || 0),
      savePercentage:
        totals.shotsAgainst > 0 ? ((totals.saves / totals.shotsAgainst) * 100).toFixed(1) : '0.0',
      goalsAgainstAverage:
        totals.minutesPlayed > 0
          ? ((totals.goalsAgainst * 60) / totals.minutesPlayed).toFixed(2)
          : '0.00',
    }),
    {}
  );

  return (
    <div className="space-y-8">
      {/* Skater Stats */}
      {skaterStats.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 text-center md:text-left">
            Skater Statistics
          </h2>

          {/* Career Totals Card */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-yellow-500 mb-4">Career Totals</h3>
            <PlayerStatsCard
              stats={skaterCareerTotals}
              variant="glass"
              className="bg-gradient-to-br from-gray-900 to-gray-800"
            />
          </div>

          {/* Season Stats */}
          {showTables ? (
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-800">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Season
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        GP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        G
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        A
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        PTS
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        PIM
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        PPG
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        SHG
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-black divide-y divide-gray-800">
                    {skaterStats.map((season, index) => (
                      <tr key={`${season.season}-${index}`} className="hover:bg-gray-900">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          <div className="flex items-center">
                            {season.season}
                            {season.isSeasonStat && (
                              <span className="ml-2 px-2 py-1 bg-green-800 text-green-200 text-xs rounded">
                                Current
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.team || 'Bombardiers'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.gp || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.g || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.a || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.pts || (season.g || 0) + (season.a || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.pim || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.ppg || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.shg || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {skaterStats.map((season, index) => (
                <Card key={`${season.season}-${index}`} className="bg-gray-900">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-white">{season.season}</h3>
                      {season.isSeasonStat && (
                        <span className="px-2 py-1 bg-green-800 text-green-200 text-xs rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <PlayerStatsCard
                      stats={{
                        gamesPlayed: season.gp,
                        goals: season.g,
                        assists: season.a,
                        penaltyMinutes: season.pim,
                        powerPlayGoals: season.ppg,
                        shortHandedGoals: season.shg,
                        overtimeGoals: season.otg,
                      }}
                      variant="glass"
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Goalie Stats */}
      {goalieStats.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 text-center md:text-left">
            Goaltending Statistics
          </h2>

          {/* Career Totals Card */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-yellow-500 mb-4">Career Totals</h3>
            <PlayerStatsCard
              stats={goalieCareerTotals}
              isGoalie={true}
              variant="glass"
              className="bg-gradient-to-br from-gray-900 to-gray-800"
            />
          </div>

          {/* Season Stats */}
          {showTables ? (
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-800">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Season
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        GP
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        SA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        GA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        SV
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        SV%
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        GAA
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        MIN
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-black divide-y divide-gray-800">
                    {goalieStats.map((season, index) => (
                      <tr key={`${season.season}-goalie-${index}`} className="hover:bg-gray-900">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          <div className="flex items-center">
                            {season.season}
                            {season.isSeasonStat && (
                              <span className="ml-2 px-2 py-1 bg-green-800 text-green-200 text-xs rounded">
                                Current
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.team || 'Bombardiers'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.gp || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.sa || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.ga || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.sv || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.svp ? `${season.svp}%` : '0.0%'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.gaa || '0.00'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {season.mins || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {goalieStats.map((season, index) => (
                <Card key={`${season.season}-goalie-${index}`} className="bg-gray-900">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-white">{season.season}</h3>
                      {season.isSeasonStat && (
                        <span className="px-2 py-1 bg-green-800 text-green-200 text-xs rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <PlayerStatsCard
                      stats={{
                        gamesPlayed: season.gp,
                        shotsAgainst: season.sa,
                        goalsAgainst: season.ga,
                        saves: season.sv,
                        savePercentage: season.svp,
                        goalsAgainstAverage: season.gaa,
                        minutesPlayed: season.mins,
                      }}
                      isGoalie={true}
                      variant="glass"
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No Stats Message */}
      {skaterStats.length === 0 && goalieStats.length === 0 && (
        <div className="bg-gray-900 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">No Statistics Available</h2>
          <p className="text-gray-400 mb-4">
            This player doesn&apos;t have any recorded statistics yet.
          </p>
          <div className="text-sm text-gray-500">
            <p>Stats will appear here once games are completed and statistics are entered.</p>
          </div>
        </div>
      )}
    </div>
  );
};
